import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Container, Tabs, Tab, Chip,
  Button, Skeleton, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions,
} from '@mui/material';
import { QrCode2, CalendarMonth, Cancel, Star, ConfirmationNumber, LocalActivity } from '@mui/icons-material';
import vouchersService, { VoucherItem } from '../../services/vouchersService';
import PivotOfferBanner from '../../components/shared/PivotOfferBanner';
import AppSnackbar from '../../components/shared/AppSnackbar';
import { GlassCard, GradientCTA, EmptyState } from '../../components/shared';
import { colors, designTokens } from '../../theme/theme';
import { useTranslation } from 'react-i18next';

const statusColors: Record<string, string> = {
  pending_match: colors.warning.main,
  active: colors.success.main,
  redeemed: colors.secondary.main,
  refunded: colors.neutral[500],
  expired: colors.error.main,
  pivoted: colors.info.main,
  cancelled: colors.neutral[400],
  floating: colors.accent.main,
};

const getStatusLabels = (t: (key: string) => string): Record<string, string> => ({
  pending_match: t('voucher.pending'),
  active: 'Ready — Show QR',
  redeemed: t('voucher.redeemed'),
  refunded: t('voucher.refunded'),
  expired: t('voucher.expired'),
  pivoted: 'Pivoted',
  cancelled: 'Cancelled',
  floating: 'Floating — Use on any event',
});

export default function VouchersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [active, setActive] = useState<VoucherItem[]>([]);
  const [history, setHistory] = useState<VoucherItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancelId, setCancelId] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [pivotOffers, setPivotOffers] = useState<any[]>([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' as 'error' | 'success' | 'info' });

  const load = async () => {
    try {
      const [a, h, p] = await Promise.all([
        vouchersService.getActive(),
        vouchersService.getHistory(),
        vouchersService.getPivotOffers().catch(() => ({ offers: [] })),
      ]);
      setActive(a.vouchers);
      setHistory(h.vouchers);
      setPivotOffers(p.offers || []);
    } catch (err) {
      console.warn('[VouchersPage] load failed', err);
      setSnackbar({ open: true, message: 'Could not load your vouchers — please try again', severity: 'error' });
    }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleCancel = async () => {
    if (!cancelId) return;
    setCancelling(true);
    try {
      await vouchersService.cancel(cancelId);
      setCancelId(null);
      load();
    } catch (e: any) { console.warn('[VouchersPage] cancel voucher failed', e); setSnackbar({ open: true, message: e.message, severity: 'error' }); }
    finally { setCancelling(false); }
  };

  const renderVoucher = (v: VoucherItem, showActions: boolean) => (
    <GlassCard key={v.voucher_id} padding={2.5} sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#FFFFFF' }}>{v.event_title}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
            <CalendarMonth sx={{ fontSize: 16, color: colors.neutral[400] }} />
            <Typography variant="body2" sx={{ color: colors.neutral[500] }}>{v.event_date || 'TBD'}</Typography>
            <Chip
              label={v.tier}
              size="small"
              sx={{
                textTransform: 'capitalize',
                fontSize: '0.7rem',
                bgcolor: 'rgba(255,255,255,0.06)',
                color: '#FFFFFF',
                border: '1px solid rgba(255,255,255,0.08)',
                fontWeight: 600,
              }}
            />
          </Box>
          {v.short_code && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="caption" sx={{ color: colors.neutral[500] }}>Code:</Typography>
              <Typography variant="body2" sx={{
                fontWeight: 700,
                fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                letterSpacing: '0.1em',
                color: v.status === 'redeemed' || v.status === 'expired' ? colors.neutral[400] : colors.primary.main,
              }}>
                {v.short_code}
              </Typography>
              {v.status === 'redeemed' && (
                <Chip
                  label="Used"
                  size="small"
                  sx={{
                    fontSize: '0.65rem', height: 18,
                    bgcolor: 'rgba(255,255,255,0.06)',
                    color: colors.neutral[500],
                    border: '1px solid rgba(255,255,255,0.08)',
                  }}
                />
              )}
            </Box>
          )}
        </Box>

        <Chip
          label={getStatusLabels(t)[v.status] || v.status}
          size="small"
          sx={{
            bgcolor: `${statusColors[v.status] || colors.neutral[400]}20`,
            color: statusColors[v.status],
            border: `1px solid ${statusColors[v.status] || colors.neutral[400]}40`,
            fontWeight: 700,
          }}
        />

        <Typography sx={{ fontWeight: 800, color: colors.primary.main, minWidth: 70, textAlign: 'right', fontSize: '1.05rem' }}>
          {v.amount} AZN
        </Typography>

        {showActions && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {(v.status === 'active' || v.status === 'pending_match') && v.qr_token && (
              <GradientCTA
                size="sm"
                onClick={() => navigate(`/vouchers/${v.voucher_id}/qr`)}
                startIcon={<QrCode2 sx={{ fontSize: 16 }} />}
              >
                {t('voucher.show_qr')}
              </GradientCTA>
            )}
            {(v.status === 'pending_match') && (
              <IconButton
                size="small"
                aria-label="Cancel voucher"
                onClick={() => setCancelId(v.voucher_id)}
                sx={{
                  color: colors.error.main,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  '&:hover': { bgcolor: colors.error.subtle },
                }}
              >
                <Cancel />
              </IconButton>
            )}
          </Box>
        )}

        {/* Floating voucher — "Use on any event" button */}
        {v.status === 'floating' && (
          <Box sx={{ width: '100%', mt: 1, pt: 1.5, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <LocalActivity sx={{ fontSize: 16, color: colors.accent.main }} />
              <Typography variant="caption" sx={{ color: colors.accent.main, fontWeight: 600 }}>
                Floating voucher — use on any {v.tier} event within 30 days
              </Typography>
            </Box>
            {v.floating_expires_at && (
              <Typography variant="caption" sx={{ color: colors.neutral[500], display: 'block', mb: 1 }}>
                Expires: {new Date(v.floating_expires_at).toLocaleDateString()}
              </Typography>
            )}
            <Button
              variant="contained" size="small"
              onClick={() => navigate(`/events?redeem=${v.voucher_id}&tier=${v.tier}`)}
              sx={{
                bgcolor: colors.accent.main, color: '#0F0F13', textTransform: 'none', fontWeight: 700, boxShadow: 'none',
                '&:hover': { bgcolor: colors.accent.light, boxShadow: '0 0 16px rgba(251,191,36,0.3)' },
              }}
            >
              Browse Events & Use Voucher
            </Button>
          </Box>
        )}

        {/* Rate button for redeemed (attended) vouchers */}
        {v.status === 'redeemed' && v.event_id && (
          <Button
            variant="outlined" size="small" startIcon={<Star />}
            onClick={() => navigate(`/post-event/${v.event_id}`)}
            sx={{
              color: colors.accent.main,
              borderColor: `${colors.accent.main}40`,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { borderColor: colors.accent.main, bgcolor: colors.accent.subtle },
            }}
          >
            Rate
          </Button>
        )}
      </Box>
    </GlassCard>
  );

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 3, color: '#FFFFFF', letterSpacing: '-0.02em' }}>
        {t('voucher.my_vouchers')}
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          mb: 3,
          '& .MuiTabs-indicator': { backgroundColor: colors.primary.main },
          '& .MuiTab-root': { color: colors.neutral[500], textTransform: 'none', fontWeight: 600 },
          '& .Mui-selected': { color: '#FFFFFF !important' },
        }}
      >
        <Tab label={`${t('voucher.active')} (${active.length})`} />
        <Tab label={`${t('voucher.history')} (${history.length})`} />
      </Tabs>

      {loading ? (
        [1, 2, 3].map(i => <Skeleton key={i} variant="rounded" height={92} sx={{ mb: 2, borderRadius: `${designTokens.radius.lg}px`, bgcolor: 'rgba(255,255,255,0.04)' }} />)
      ) : tab === 0 ? (
        active.length === 0 ? (
          <EmptyState
            icon={<ConfirmationNumber />}
            title="No active vouchers"
            description="Browse events and join a table — your tickets land here."
            cta={
              <GradientCTA size="md" onClick={() => navigate('/')}>
                Browse events
              </GradientCTA>
            }
          />
        ) : (
          <>
            {pivotOffers.map((offer: any) =>
              offer.alternatives.map((alt: any) => (
                <PivotOfferBanner
                  key={`${offer.voucher_id}-${alt.event_id}`}
                  offer={{
                    voucher_id: offer.voucher_id,
                    original_event_title: offer.original_event_title,
                    alternative_event_id: alt.event_id,
                    alternative_event_title: alt.title,
                    alternative_venue_name: alt.venue_name || 'Nearby venue',
                    spots_available: alt.spots_available,
                  }}
                  onResolved={load}
                />
              ))
            )}
            {active.map(v => renderVoucher(v, true))}
          </>
        )
      ) : (
        history.length === 0 ? (
          <EmptyState
            icon={<ConfirmationNumber />}
            title="Quiet so far"
            description="Your past vouchers will live here once you've attended an event."
            py={6}
          />
        ) : history.map(v => renderVoucher(v, false))
      )}

      {/* Cancel Confirmation */}
      <Dialog open={!!cancelId} onClose={() => setCancelId(null)}>
        <DialogTitle>{t('voucher.cancel')}?</DialogTitle>
        <DialogContent>
          <Typography>You'll receive a full refund. This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelId(null)}>Keep It</Button>
          <Button variant="contained" color="error" onClick={handleCancel} disabled={cancelling}>
            {cancelling ? 'Cancelling...' : 'Cancel & Refund'}
          </Button>
        </DialogActions>
      </Dialog>

      <AppSnackbar open={snackbar.open} message={snackbar.message} severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))} />
    </Container>
  );
}
